import React, { FC, useMemo } from "react";
import { Controller, useFormContext } from "react-hook-form";
import TextField from "@mui/material/TextField";
import { AddOUFormData } from "./validation.schema";
import Select from "@/components/Select/index";
import { useTranslation } from "react-i18next";
import Grid from "@mui/material/Grid2";
import { useGetOUGroupLabels } from "@/hooks/management/useOrganizations.manage";
import { Tooltip } from "@mui/material";

type TabOrganizationGeneralProps = {
  editMode?: boolean;
};

export const TabOrganizationGeneral: FC<TabOrganizationGeneralProps> = ({
  editMode = false,
}) => {
  const { t } = useTranslation("organization");

  const { data, isLoading } = useGetOUGroupLabels();

  const groupLabelsFormatted = useMemo(() => {
    if (!data) return [];

    return data?.map((groupLabel) => ({
      key: groupLabel._id,
      value: groupLabel.name,
    }));
  }, [data]);

  const {
    control,
    formState: { errors },
  } = useFormContext<AddOUFormData>();

  return (
    <Grid container spacing={"20px"}>
      <Grid size={6}>
        <Controller
          name="name"
          control={control}
          defaultValue=""
          render={({ field }) => (
            <TextField
              {...field}
              id="name"
              label={t("modals.label-name")}
              variant="outlined"
              required
              value={field.value}
              onChange={(value) => field.onChange(value)}
              error={!!errors.name}
              helperText={errors.name ? errors.name.message : null}
              fullWidth
            />
          )}
        />
      </Grid>
      <Tooltip
        placement="top-start"
        title="Group label allows you to group your organizational units based on your prefrered criteria."
      >
        <Grid size={6}>
          <Controller
            name="groupLabelIds"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <Select
                handleChange={(value) => {
                  field.onChange(value);
                }}
                data={groupLabelsFormatted || []}
                value={(field.value as string[]) || []}
                label={t("modals.label-grouplabel")}
                id="grouplabel"
                isLoading={isLoading}
                fullWidth
                multiple
                error={!!errors.groupLabelIds}
                errorMessage={errors.groupLabelIds?.message}
              />
            )}
          />
        </Grid>
      </Tooltip>

      <Grid size={6}>
        <Controller
          name="description"
          control={control}
          defaultValue=""
          render={({ field }) => (
            <TextField
              {...field}
              id="description"
              label={t("modals.label-description")}
              variant="outlined"
              value={field.value}
              onChange={(value) => field.onChange(value)}
              error={!!errors.description}
              helperText={
                errors.description ? errors.description.message : null
              }
              fullWidth
              multiline
              maxRows={4}
              rows={4}
            />
          )}
        />
      </Grid>

      {editMode && (
        <Grid size={6}>
          <Controller
            name="status"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <Select
                handleChange={(value) => {
                  field.onChange(value);
                }}
                data={[
                  { key: "ACTIVE", value: "Active" },
                  { key: "INACTIVE", value: "Inactive" },
                ]}
                value={(field.value as string[]) || []}
                label={t("modals.label-status")}
                required
                id="statusLabel"
                isLoading={isLoading}
                fullWidth
                error={!!errors.groupLabelIds}
                errorMessage={errors.groupLabelIds?.message}
              />
            )}
          />
        </Grid>
      )}
    </Grid>
  );
};
